// Sentry first: every other import's module-level code then runs with error reporting installed.
import "./sentry.ts";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
  redirect,
  useNavigate,
  useParams,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { z } from "zod";
import {
  DEFAULT_SETTINGS_SECTION,
  type DetachedScope,
  detachedSettingsSection,
  movedSettingsSection,
  parseStandingAnswerFocus,
  type SettingsSection,
  settingsRedirect,
  settingsSectionFor,
} from "../src/settings-sections.ts";
import { providePageRequest } from "./agent/page-request.ts";
import { OFFER_QUERY } from "./draft-again.tsx";
import { SAVE_REFUSED, WorkspaceFaq } from "./faq.tsx";
import { prefetchInboxFirstReads, useRememberInbox } from "./inbox-v2/first-reads.ts";
import { FolderPage } from "./inbox-v2/folder-page.tsx";
import { MailboxPage } from "./inbox-v2/mailbox-page.tsx";
import { InboxV2Page } from "./inbox-v2/page.tsx";
import { useInboxAccess } from "./inbox-v2/use-inbox.ts";
import "./styles.css";
import { ApiError, retryRead, retryReadDelay } from "./http.ts";
import { InboxOff } from "./inbox-off.tsx";
import { lazyScreen } from "./lazy-screen.tsx";
import { accessQuery, sessionQuery, workspacesQuery } from "./queries.ts";
import { AcceptInvite } from "./shell/accept-invite.tsx";
import { AppShell, LoadFailedPage, rememberWorkspace, type Shell } from "./shell/app-shell.tsx";
import { ForgotPassword } from "./shell/forgot-password.tsx";
import { ResetPassword } from "./shell/reset-password.tsx";
import { answersFocusPath, answersPath, campaignsPath, inboxPath } from "./workspace-context.tsx";
import { idOfWorkspace, slugOfWorkspace } from "./workspace-slug.ts";

// Downloaded when first opened (`lazy-screen.tsx`). Standing answers stays in the bundle: the inbox's
// agent pane draws its answer fields from the same module.
const CampaignPage = lazyScreen(() => import("./campaign-page.tsx").then((m) => m.CampaignPage));
const CampaignsList = lazyScreen(() => import("./campaigns.tsx").then((m) => m.CampaignsList));
const MailboxesPage = lazyScreen(() => import("./mailboxes.tsx").then((m) => m.MailboxesPage));
const WorkspaceSettings = lazyScreen(() =>
  import("./workspace-settings.tsx").then((m) => m.WorkspaceSettings),
);
const DetachedSettings = lazyScreen(() =>
  import("./workspace-settings.tsx").then((m) => m.DetachedSettings),
);
const AccountPageSettings = lazyScreen(() =>
  import("./workspace-settings.tsx").then((m) => m.AccountPageSettings),
);
const McpConsent = lazyScreen(() => import("./mcp-auth/consent.tsx").then((m) => m.McpConsent));
const McpConnections = lazyScreen(() =>
  import("./mcp-auth/connections.tsx").then((m) => m.McpConnections),
);

/** What a refused write says, by the refusal's code; any other conflict is a changed conversation. */
const CONFLICT_MESSAGE: Readonly<Record<string, string>> = {
  already_answered: "This question was already answered. The current answer is shown below.",
  question_closed:
    "This answer was not saved: the question is closed, and the app is no longer waiting for it.",
  material_not_ready: "This draft is waiting for its file. Upload it before approving.",
  slots_unresolved: "This draft has parts only you can confirm. Confirm them before approving.",
  redraft_in_progress:
    "Not approved: another rep asked for a new draft of this reply, and it is being written.",
  update_required:
    "Nothing changed: this draft has files that this version of the app cannot send. Try again after the app is updated.",
};
const conflictMessage = (code: string | null): string =>
  (code !== null && Object.hasOwn(CONFLICT_MESSAGE, code) ? CONFLICT_MESSAGE[code] : undefined) ??
  "This conversation changed. Refresh before continuing.";

/**
 * A 409's sentence. A refusal that shows the page is behind the server (it drew a question as open
 * that the server holds closed) also has what is offered read again, so the card is redrawn.
 */
function refusedBy(code: string | null): string {
  // A question drawn open that is closed, or a draft drawn that another rep asked to replace.
  if (code === "question_closed" || code === "redraft_in_progress")
    void queryClient.invalidateQueries({ queryKey: [OFFER_QUERY] });
  return conflictMessage(code);
}

/** Why the save route refused typed slots (`src/call-length.ts`), in the rep's words. */
const SLOT_REFUSALS: Readonly<Record<string, string>> = {
  slot_length:
    "Each slot must be one call long, and the call length has changed. Refresh this conversation, then save again.",
  call_length_first: "Answer how long the call is first, then add the slots.",
  clock:
    "One of these times happens twice, or not at all, when the clocks change. Choose another time.",
};
/** What a refused answer says: why slots were refused when that is known, else which answer. */
function invalidAnswerMessage(key: string | undefined, reason: string | undefined): string {
  const slots =
    reason !== undefined && Object.hasOwn(SLOT_REFUSALS, reason)
      ? SLOT_REFUSALS[reason]
      : undefined;
  return (
    slots ??
    `This answer cannot be used for ${key ?? "this question"}. Check the fields and try again.`
  );
}

/**
 * What to say of a failure the server gave no name. A question card's save (`…/answers`) reads as
 * Standing answers' save does: it approves nothing, so it does not speak of approval.
 */
const unnamedFailure = (path: string): string =>
  path.endsWith("/answers")
    ? SAVE_REFUSED
    : "The request could not be completed. Your work has not been approved.";

async function request(path: string, body?: object): Promise<unknown> {
  const response = await fetch(
    path,
    body === undefined
      ? { credentials: "same-origin" }
      : {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(body),
        },
  );
  if (!response.ok) {
    const failure = z
      .object({ error: z.string(), key: z.string().optional(), reason: z.string().optional() })
      .safeParse(await response.json().catch(() => null));
    throw new Error(
      response.status === 409
        ? refusedBy(failure.success ? failure.data.error : null)
        : response.status === 400 && failure.success && failure.data.error === "invalid_answer"
          ? invalidAnswerMessage(failure.data.key, failure.data.reason)
          : response.status === 422 && failure.success && failure.data.error === "scope_not_allowed"
            ? "This answer can only be saved for this conversation. Choose “This conversation” and save again."
            : response.status === 423
              ? "Still importing this campaign's history. Try again when it finishes."
              : response.status === 401
                ? "Please sign in again."
                : unnamedFailure(path),
    );
  }
  return response.json();
}
/** The work pane (`agent/work-pane.tsx`) reaches this page's `request` through its registry. */
providePageRequest(request);
const queryClient = new QueryClient({
  // Freshness is each read's own (`queries.ts`); a transient failure is tried once more (W4).
  defaultOptions: {
    queries: { retry: retryRead, retryDelay: retryReadDelay, refetchOnWindowFocus: true },
  },
  /*
   * A read answered 401: the session ended, so the session is read again, and the shell drops what
   * was cached for that person once it answers. A 403 on a workspace's own read: this person may
   * have lost that workspace, so the list and that workspace's access are read again, and a
   * workspace no longer listed is no longer drawn (mitigation plan, W3). A 404 naming the workspace:
   * it was offboarded while the page was open, so the list is read again and the shell says the
   * workspace is not available.
   */
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (!(error instanceof ApiError)) return;
      const [root, workspaceId] = query.queryKey;
      if (error.signedOut && root !== "session")
        void queryClient.invalidateQueries({ queryKey: ["session"] });
      if (error.workspaceGone && root !== "workspaces")
        void queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      if (error.refused && root === "inbox-v2" && typeof workspaceId === "string") {
        void queryClient.invalidateQueries({ queryKey: ["workspaces"] });
        void queryClient.invalidateQueries({ queryKey: ["inbox-v2-access", workspaceId] });
      }
    },
  }),
  /*
   * A page left open while its workspace was offboarded meets 404 `workspace_not_found` on its next
   * write. Any failed write re-reads the workspace list, so the shell says the workspace is not
   * available instead of offering the same refused control again.
   */
  mutationCache: new MutationCache({
    onError: () => void queryClient.invalidateQueries({ queryKey: ["workspaces"] }),
  }),
});

type Page = "settings" | "answers" | "campaigns" | "campaign" | "mailboxes";

/*
 * The pages of one workspace, inside the app shell (`shell/app-shell.tsx`), which owns the rail, the
 * session, the workspace list, the theme and the pane layout. Each workspace mounts a fresh view, so
 * nothing typed in one carries into another.
 */
function Workspace({
  shell,
  page,
  section,
  sectionNamed,
  campaignId,
  replace,
}: {
  shell: Shell;
  page: Page;
  /** Moves to another address in place of this one, as a redirect does. */
  replace: (href: string) => void;
  /** The campaign whose page is open, on the campaign page only. */
  campaignId: string | null;
  /** The settings section the URL resolved to, or null when it named one this app does not have. */
  section: SettingsSection | null;
  /** What the URL actually said, so an unknown section can be named back to the rep. */
  sectionNamed: string | null;
}) {
  const { current } = shell;
  switch (page) {
    case "answers":
      /*
       * Standing answers is its own resource now. A campaign held because its copy disagrees
       * with a confirmed fact links here with that fact and campaign in the URL, so the way
       * out of the hold survives a navigation and can be reloaded or shared.
       */
      return (
        <main className="lcr-settings lcr-answers">
          <WorkspaceFaq
            key={current.id}
            workspaceId={current.id}
            focus={parseStandingAnswerFocus(location.search)}
            onFocused={() => history.replaceState(null, "", answersPath(current.id))}
          />
        </main>
      );
    case "campaigns":
      return (
        <main className="lcr-settings lcr-campaigns-main" aria-label="Campaigns">
          <CampaignsList key={current.id} onNavigate={shell.leave} />
        </main>
      );
    case "mailboxes":
      return (
        <main className="lcr-settings lcr-campaigns-main" aria-label="Mailboxes">
          <MailboxesPage key={current.id} />
        </main>
      );
    case "campaign":
      return campaignId === null ? null : (
        <main className="lcr-settings lcr-campaigns-main" aria-label="Campaign">
          <CampaignPage
            key={`${current.id}:${campaignId}`}
            campaignId={campaignId}
            onNavigate={shell.leave}
            onStandingAnswer={(focus) => shell.leave(answersFocusPath(current.id, focus))}
          />
        </main>
      );
    case "settings":
      return (
        <WorkspaceSettings
          key={current.id}
          section={section}
          sectionNamed={sectionNamed}
          onNavigate={shell.leave}
          onReplace={replace}
          user={shell.user}
          dark={shell.dark}
          onToggleTheme={shell.toggleTheme}
          onSignOut={shell.signOut}
        />
      );
  }
}

/** Each workspace and page mounts its own view, so no state crosses from one to another. */
function WorkspaceRoute({ page }: { page: Page }) {
  const { workspaceId, section, campaignId } = useParams({ strict: false });
  const id = workspaceId ?? null;
  const navigate = useNavigate();
  /*
   * A settings section that moved out of the settings area opens where it went. Sign-off is on each
   * campaign's page now, so its old address opens the campaigns list instead of "No such page".
   */
  const moved = page === "settings" ? movedSettingsSection(section) : null;
  useEffect(() => {
    if (moved !== null && id !== null) void navigate({ href: campaignsPath(id), replace: true });
  }, [moved, id, navigate]);
  if (moved !== null) return null;
  /*
   * A settings URL that names no section lands on the default one; a URL that names a section this
   * app does not have resolves to null, and the area says so rather than quietly showing another.
   */
  const settingsSection =
    page === "settings" ? settingsSectionFor(section ?? DEFAULT_SETTINGS_SECTION) : null;
  return (
    <AppShell
      key={`${id ?? ""}:${page}`}
      workspaceId={id}
      page={page}
      section={settingsSection}
      request={request}
    >
      {(shell) => (
        <Workspace
          shell={shell}
          page={page}
          campaignId={page === "campaign" ? (campaignId ?? null) : null}
          section={settingsSection}
          sectionNamed={page === "settings" ? (section ?? null) : null}
          replace={(href) => void navigate({ href, replace: true })}
        />
      )}
    </AppShell>
  );
}
/**
 * An agency or account settings page, or bare `/settings`, inside the shell with no workspace in its
 * address. The page reads the remembered workspace from the shell, which may be none.
 */
function DetachedSettingsRoute({ scope }: { scope: DetachedScope | null }) {
  const { section } = useParams({ strict: false });
  const navigate = useNavigate();
  const found = scope === null ? null : detachedSettingsSection(scope, section);
  return (
    <AppShell
      key={`settings:${scope ?? ""}`}
      workspaceId={null}
      page="settings"
      section={found}
      request={request}
      detachedPage={(shell) => (
        <DetachedSettings
          key={shell.current?.id ?? ""}
          workspace={shell.current}
          section={found}
          sectionNamed={scope === null ? null : (section ?? "")}
          onNavigate={shell.leave}
          onReplace={(href) => void navigate({ href, replace: true })}
          user={shell.user}
          dark={shell.dark}
          onToggleTheme={shell.toggleTheme}
          onSignOut={shell.signOut}
        />
      )}
    >
      {() => null}
    </AppShell>
  );
}
/*
 * The master inbox (`web/inbox-v2/`), as a page inside the app shell like every other (master-inbox
 * plan, step 2). Its address is the workspace's own, `/w/<id>`, where the list it shows is in the
 * query (step 10).
 */
function MailRoute() {
  const { workspaceId } = useParams({ strict: false });
  const mailbox = useRouterState({
    select: (state) => new URLSearchParams(state.location.searchStr).get("mailbox") === "all",
  });
  const id = workspaceId ?? null;
  return (
    <AppShell
      key={`${id ?? ""}:mail`}
      workspaceId={id}
      page="mail"
      section={null}
      request={request}
    >
      {(shell) =>
        mailbox ? (
          <MailboxPage key={shell.current.id} shell={shell} />
        ) : shell.folder ? (
          <FolderPage shell={shell} folder={shell.folder} />
        ) : (
          <InboxV2Page shell={shell} />
        )
      }
    </AppShell>
  );
}
/*
 * A workspace whose inbox is switched off: one sentence, and its own pages for a person with the
 * reply agent (`inbox-off.tsx`). The rail's Inbox row is this page.
 */
function InboxOffRoute() {
  const { workspaceId } = useParams({ strict: false });
  const id = workspaceId ?? null;
  return (
    <AppShell
      key={`${id ?? ""}:inbox-off`}
      workspaceId={id}
      page="inbox"
      section={null}
      request={request}
    >
      {(shell) => <InboxOff workspaceId={shell.current.id} others={shell.others} />}
    </AppShell>
  );
}
/*
 * Everyone on an inbox workspace gets the master inbox at the workspace's own address, with or without
 * the reply agent (step 10): the agent's states and material are drawn inside it for whoever holds
 * the agent. A workspace whose inbox is switched off gets one sentence in its place (C4); no route
 * draws the old inbox any more. The API decides what each may reach (`route-access.ts`); this only
 * chooses the screen.
 */
function WorkspaceInboxRoute() {
  const { workspaceId } = useParams({ strict: false });
  const access = useInboxAccess(workspaceId);
  useRememberInbox(workspaceId, access.data?.inbox);
  /*
   * A first access read that failed is not "the inbox is off": the shell says it failed (W3). It
   * stays said while the read is tried again: a read with no answer yet goes back to pending when
   * it refetches, and drawing nothing then would remount the shell, whose mount asks again.
   */
  if (workspaceId && access.data === undefined && (access.isError || access.errorUpdatedAt > 0))
    return <AccessFailedRoute onRetry={() => void access.refetch()} />;
  if (workspaceId && access.isPending) return null;
  if (workspaceId && access.data?.inbox) return <MailRoute />;
  return <InboxOffRoute />;
}
/** The workspace's access could not be read and never was: said in the shell, with Try again. */
function AccessFailedRoute(props: { onRetry: () => void }) {
  const { workspaceId } = useParams({ strict: false });
  const id = workspaceId ?? null;
  return (
    <AppShell
      key={`${id ?? ""}:access-failed`}
      workspaceId={id}
      page="mail"
      section={null}
      request={request}
    >
      {() => (
        <main className="lcr-settings">
          <LoadFailedPage what="this workspace's inbox" onRetry={props.onRetry} />
        </main>
      )}
    </AppShell>
  );
}
/*
 * `/` is not a view: the shell resolves it to a workspace's own address (`useResolvedWorkspace`) and
 * draws nothing of its own meanwhile.
 */
function ResolveWorkspace() {
  return (
    <AppShell workspaceId={null} page="mail" section={null} request={request}>
      {() => null}
    </AppShell>
  );
}
function AccountRoute({ consent = false }: { consent?: boolean }) {
  // The provider signs repeated ba_param fields; the router's normalized search collapses them.
  const search = useRouterState({ select: () => window.location.search });
  return (
    <AppShell
      workspaceId={null}
      page="account"
      section={null}
      request={request}
      personalPage={
        consent ? (
          <McpConsent oauthQuery={search.replace(/^\?/, "")} />
        ) : (
          // Listed in Settings › Account, so drawn in its column (LEA-501).
          <AccountPageSettings slug="connections">
            <McpConnections />
          </AccountPageSettings>
        )
      }
    >
      {() => null}
    </AppShell>
  );
}
/**
 * The master inbox's address before step 10: a bookmark or a shared link opens the same list and
 * conversation at the workspace's own address, its query and hash kept, in place.
 */
function OldMailAddress() {
  const { workspaceId } = useParams({ strict: false });
  const navigate = useNavigate();
  useEffect(() => {
    if (!workspaceId) return;
    const target = `${inboxPath(workspaceId)}${location.search}${location.hash}`;
    void navigate({ href: target, replace: true });
  }, [workspaceId, navigate]);
  return null;
}
/*
 * Every page waits on the session, the workspace list and, in a workspace, its access. They are
 * asked together as the address changes, not each after the one before (W4, F09): the shell and the
 * routes then read the same cache entries. Nothing here is awaited, so no page waits on a badge.
 */
function prefetchShell(workspaceId?: string): void {
  void queryClient.prefetchQuery(sessionQuery());
  void queryClient.prefetchQuery(workspacesQuery());
  if (workspaceId) void queryClient.prefetchQuery(accessQuery(workspaceId));
}
const inWorkspace = ({ params }: { params: { workspaceId: string } }) =>
  prefetchShell(params.workspaceId);
/** The inbox's own first reads start with the shell's, not after its access answer. */
const inInbox = (route: { params: { workspaceId: string }; location: { searchStr: string } }) => {
  inWorkspace(route);
  prefetchInboxFirstReads(queryClient, route.params.workspaceId, route.location.searchStr);
};
/*
 * A workspace's address names its slug (`workspace-slug.ts`, LEA-499). Parsing turns it back into the
 * id that every component and request uses, and stringifying turns an id into its slug. An address
 * that names the id (a server-built link, an older bookmark) is replaced by the slug's.
 */
function workspaceParamsWith<K extends string = never>() {
  type Params = { workspaceId: string } & Record<K, string>;
  return {
    parse: (raw: Params): Params => ({ ...raw, workspaceId: idOfWorkspace(raw.workspaceId) }),
    stringify: (params: Params): Params => ({
      ...params,
      workspaceId: slugOfWorkspace(params.workspaceId),
    }),
  };
}
const workspaceParams = workspaceParamsWith();
function canonicalWorkspaceAddress(route: {
  location: { href: string; pathname: string };
  params: { workspaceId: string };
}): void {
  const slug = encodeURIComponent(slugOfWorkspace(route.params.workspaceId));
  const named = route.location.pathname.split("/")[2];
  if (named === undefined || named === slug) return;
  throw redirect({
    href: route.location.href.replace(/^\/w\/[^/?#]*/, `/w/${slug}`),
    replace: true,
  });
}
const rootRoute = createRootRoute({ component: () => <Outlet /> });
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  loader: () => prefetchShell(),
  component: ResolveWorkspace,
});
const inboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/w/$workspaceId",
  params: workspaceParams,
  beforeLoad: canonicalWorkspaceAddress,
  loader: inInbox,
  component: WorkspaceInboxRoute,
});
const inboxV2Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/w/$workspaceId/inbox",
  params: workspaceParams,
  beforeLoad: canonicalWorkspaceAddress,
  component: OldMailAddress,
});
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/w/$workspaceId/settings",
  params: workspaceParams,
  beforeLoad: canonicalWorkspaceAddress,
  loader: inWorkspace,
  component: () => <WorkspaceRoute page="settings" />,
});
const settingsSectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/w/$workspaceId/settings/$section",
  params: workspaceParamsWith<"section">(),
  /*
   * Agency and account settings left the workspace's address (LEA-501). An old link opens the new
   * address in its place, remembering the workspace it named so the rail still shows that client.
   */
  beforeLoad: (route) => {
    canonicalWorkspaceAddress(route);
    const moved = settingsRedirect(route.params.section);
    if (moved === null) return;
    rememberWorkspace(route.params.workspaceId);
    throw redirect({ href: moved, replace: true });
  },
  loader: inWorkspace,
  component: () => <WorkspaceRoute page="settings" />,
});
/*
 * Settings outside any workspace (LEA-501): the agency's (people, workspaces) and the person's own.
 * Bare `/settings` opens the remembered workspace's settings, or with none, the agency's Workspaces.
 */
const detachedSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  loader: () => prefetchShell(),
  component: () => <DetachedSettingsRoute scope={null} />,
});
const agencySettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/agency/$section",
  loader: () => prefetchShell(),
  component: () => <DetachedSettingsRoute scope="agency" />,
});
const accountSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/account/$section",
  // A section Settings lists but does not draw (Connected apps) opens at its own address.
  beforeLoad: ({ params }) => {
    const own = detachedSettingsSection("account", params.section)?.href;
    if (own) throw redirect({ href: own, replace: true });
  },
  loader: () => prefetchShell(),
  component: () => <DetachedSettingsRoute scope="account" />,
});
const answersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/w/$workspaceId/answers",
  params: workspaceParams,
  beforeLoad: canonicalWorkspaceAddress,
  loader: inWorkspace,
  component: () => <WorkspaceRoute page="answers" />,
});
const campaignsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/w/$workspaceId/campaigns",
  params: workspaceParams,
  beforeLoad: canonicalWorkspaceAddress,
  loader: inWorkspace,
  component: () => <WorkspaceRoute page="campaigns" />,
});
const mailboxesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/w/$workspaceId/mailboxes",
  params: workspaceParams,
  beforeLoad: canonicalWorkspaceAddress,
  loader: inWorkspace,
  component: () => <WorkspaceRoute page="mailboxes" />,
});
const campaignRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/w/$workspaceId/campaigns/$campaignId",
  params: workspaceParamsWith<"campaignId">(),
  beforeLoad: canonicalWorkspaceAddress,
  loader: inWorkspace,
  component: () => <WorkspaceRoute page="campaign" />,
});
/*
 * The signed-out account pages (flows F3 and F6). They draw their own frame, the sign-in's, and read
 * no session or workspace first: a person following an email link may have neither.
 */
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPassword,
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPassword,
});
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite",
  component: AcceptInvite,
});
const mcpConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/oauth/consent",
  loader: () => void queryClient.prefetchQuery(sessionQuery()),
  component: () => <AccountRoute consent />,
});
const mcpConnectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account/connections",
  loader: () => void queryClient.prefetchQuery(sessionQuery()),
  component: () => <AccountRoute />,
});
const router = createRouter({
  routeTree: rootRoute.addChildren([
    indexRoute,
    inboxRoute,
    inboxV2Route,
    settingsRoute,
    settingsSectionRoute,
    detachedSettingsRoute,
    agencySettingsRoute,
    accountSettingsRoute,
    answersRoute,
    campaignsRoute,
    campaignRoute,
    mailboxesRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    inviteRoute,
    mcpConsentRoute,
    mcpConnectionsRoute,
  ]),
});
const element = document.getElementById("root");
// The boot recovery in `index.html` may load a second copy of this bundle; only one mounts.
// Keep the root handle so a host mounting the entry point can also unmount its subscriptions.
export const appRoot = element && !window.__leadcueReplyBooted ? createRoot(element) : null;
if (appRoot) {
  window.__leadcueReplyBooted = true;
  /*
   * The first route match parses a workspace slug, which needs the workspace list, so the app draws
   * once that list has been read (or refused, signed out). Personal OAuth pages need only the
   * current session; a stalled workspace directory must not prevent consent or disconnect.
   */
  const path = window.location.pathname.replace(/\/+$/, "");
  const personal = ["/oauth/consent", "/account/connections"].includes(path);
  const ready = personal ? Promise.resolve() : queryClient.prefetchQuery(workspacesQuery());
  void ready.finally(() =>
    appRoot.render(
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>,
    ),
  );
}
